import React, { useState } from 'react'
import { Box, IconButton, Popover } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined'
import CheckIcon from '@mui/icons-material/Check'
import { useI18n } from '@/contexts/I18nContext'
import { useThemeAccent } from '@/contexts/ThemeAccentContext'
import { buildKiotHeaderIconButtonSx } from '@/components/layout/banhangKiotTokens'
import { uiPalette } from '@/theme/uiPalette'

type ThemeAccentPickerProps = {
  /** `header`: nút trên navbar. `surface`: nút trên nền login. `menu`: lưới màu nhúng trong menu account. */
  variant?: 'header' | 'surface' | 'menu'
  /** Gọi sau khi chọn accent (vd. đóng menu account). */
  onPicked?: () => void
}

const ThemeAccentSwatchGrid: React.FC<{
  onPicked?: () => void
  /** `vertical`: xếp màu theo cột (submenu account). */
  layout?: 'grid' | 'vertical'
}> = ({ onPicked, layout = 'grid' }) => {
  const { accentId, setAccentId, presets } = useThemeAccent()
  const isVertical = layout === 'vertical'

  return (
    <Box
      sx={
        isVertical
          ? {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 0.75,
              minWidth: 44,
            }
          : {
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1.25,
              width: 132,
            }
      }
    >
      {presets.map((p) => {
        const selected = p.id === accentId
        return (
          <Box
            key={p.id}
            component="button"
            type="button"
            aria-label={p.id}
            aria-pressed={selected}
            onClick={() => {
              setAccentId(p.id)
              onPicked?.()
            }}
            sx={{
              width: isVertical ? '100%' : 36,
              height: 36,
              borderRadius: '6px',
              border: selected ? '2px solid rgba(0,0,0,0.45)' : '2px solid rgba(0,0,0,0.12)',
              bgcolor: p.main,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 0,
              m: 0,
              boxSizing: 'border-box',
              transition: 'transform 80ms ease, border-color 120ms ease',
              '&:hover': { transform: 'scale(1.04)' },
            }}
          >
            {selected ? (
              <CheckIcon
                sx={{
                  color: '#fff',
                  fontSize: 22,
                  filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.55))',
                }}
              />
            ) : null}
          </Box>
        )
      })}
    </Box>
  )
}

const ThemeAccentPicker: React.FC<ThemeAccentPickerProps> = ({ variant = 'header', onPicked }) => {
  const { t } = useI18n()
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)
  const isSurface = variant === 'surface'
  const surfaceBorder = theme.palette.banhang?.primarySurfaceBorder ?? uiPalette.primary.surfaceBorder
  const surfaceBg = theme.palette.banhang?.primarySurface ?? uiPalette.primary.surface

  if (variant === 'menu') {
    return <ThemeAccentSwatchGrid layout="vertical" onPicked={onPicked} />
  }

  const headerIconSx = {
    ...buildKiotHeaderIconButtonSx({
      activeBg: theme.palette.banhang.navItemActiveBg,
      activeBgHover: theme.palette.banhang.navItemActiveBgHover,
    }),
    height: '100%',
    minHeight: 0,
    alignSelf: 'stretch',
  }

  return (
    <>
      <IconButton
        color="inherit"
        size={isSurface ? 'medium' : 'small'}
        aria-label={t('common.themeAccent.aria')}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={
          isSurface
            ? {
                color: theme.palette.primary.main,
                border: `1px solid ${surfaceBorder}`,
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                boxShadow: '0 2px 8px rgba(15, 23, 42, 0.08)',
                '&:hover': {
                  backgroundColor: surfaceBg,
                },
              }
            : headerIconSx
        }
      >
        <PaletteOutlinedIcon sx={{ fontSize: 20 }} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              p: 1.5,
              mt: 0.25,
              borderRadius: '10px',
              border: '1px solid rgba(0, 0, 0, 0.14)',
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
              backgroundImage: 'none',
            },
          },
        }}
      >
        <ThemeAccentSwatchGrid
          onPicked={() => {
            setAnchorEl(null)
            onPicked?.()
          }}
        />
      </Popover>
    </>
  )
}

export default ThemeAccentPicker
